/* eslint-disable react/no-multi-comp */
import ContentWrapper from "@/components/common/contentWrapper/ContentWrapper";
import PageWrapper from "@/components/common/pageWrapper/PageWrapper";
import BlogArticle from "@/components/pages/blogOverview/blogArticle/BlogArticle";
import CategorySelectButton from "@/components/pages/blogOverview/categorySelectButton/CategorySelectButton";
import { type GetBlogOverviewPagePropsResult } from "@/pages/blog";
import { type GetNewsOverviewPagePropsResult } from "@/pages/news";
import {
  type IGenBlogArticle,
  type IGenBlogCategory, type IGenBlogOverviewPage, type IGenFooter, type IGenHeader,
  type IGenNewsArticle,
  type IGenNewsCategory, type IGenNewsOverviewPage, type IGenSeo
} from "@/services/graphql/__generated/sdk";
import { animationProps } from "@/styles/styleVariables";
import { removeDuplicatesFromObjectArrayBasedOnKey } from "@/utils/removeDuplicates";
import { type NewsOrBlog } from "@/utils/types";

import { motion } from "framer-motion";
import React, { type FunctionComponent, useState } from "react";

import * as styles from "./BlogOverview.page.style";

interface BlogOverviewPageContentProps
{
  readonly articleOverviewPageProps: IGenNewsOverviewPage | IGenBlogOverviewPage;
  readonly articles: (IGenNewsArticle | IGenBlogArticle)[];
  readonly footer: IGenFooter;
  readonly header: IGenHeader;
  readonly newsOrBlog: NewsOrBlog;
  readonly seo: IGenSeo;
}

const BlogOverviewPageContent: FunctionComponent<BlogOverviewPageContentProps> = ({
  articleOverviewPageProps, 
  articles,
  footer,
  header,
  newsOrBlog,
  seo,
}) =>
{
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);

  let featuredArticle: (IGenBlogArticle | IGenNewsArticle) | undefined;
  let featuredArticleSlug: string | undefined;

  if(selectedCategoryId == null)
  {
    featuredArticle = articleOverviewPageProps.highlightedBlogArticle || articles[0];
    featuredArticleSlug = featuredArticle?.slug || undefined;
  }

  const blogArticlesWithoutFeatured = articles.filter(article => article.slug !== featuredArticleSlug);
  const allCategories = articles.map(article => article.category).filter(Boolean);
  const uniqueCategories = removeDuplicatesFromObjectArrayBasedOnKey<IGenBlogCategory | IGenNewsCategory>(allCategories, "id");

  let blogArticlesFromActiveCategory: (IGenBlogArticle | IGenNewsArticle)[];

  if(selectedCategoryId == null)
  {
    blogArticlesFromActiveCategory = blogArticlesWithoutFeatured;
  }
  else
  {
    blogArticlesFromActiveCategory = blogArticlesWithoutFeatured.filter(article => article.category?.id === selectedCategoryId);
  }

  const isValidDate = (date: any): boolean => date != null && !isNaN(new Date(date).getDate());

  const blogArticlesFromActiveCategoryWithValidDates = blogArticlesFromActiveCategory.filter(article => isValidDate(article.date));
  const blogArticlesFromActiveCategoryWithInvalidDates = blogArticlesFromActiveCategory.filter(article => !isValidDate(article.date));

  const blogArticlesFromActiveCategoryWithValidDatesSortedByDate = blogArticlesFromActiveCategoryWithValidDates.filter(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    article => isValidDate(new Date(article.date))).sort((a, b) => new Date(b.date) - new Date(a.date)
  );

  const blogArticlesFromActiveCategorySortedByDate = [...blogArticlesFromActiveCategoryWithValidDatesSortedByDate, ...blogArticlesFromActiveCategoryWithInvalidDates];

  return (
    <PageWrapper
      footerProps={footer}
      headerProps={{
        ...header,
        isHeaderFloating: false
      }}
      websiteSeo={seo}
      pageTitle={articleOverviewPageProps.title || ""}
      pageSeo={articleOverviewPageProps.pageSeo}>
      <ContentWrapper>
        <motion.div {...animationProps}>
          <h1 css={styles.pageTitle}>{articleOverviewPageProps.title}</h1>
          {uniqueCategories?.length > 0 && (
            <div css={styles.categoriesSelectWrapper}>
              <CategorySelectButton
                key="all-categories"
                id={undefined}
                title={articleOverviewPageProps.allArticlesCategoryButtonText}
                setSelectedCategoryId={setSelectedCategoryId}
                isActive={selectedCategoryId == null}
              />
              {uniqueCategories.map(category => (
                <CategorySelectButton
                  key={category.id}
                  title={category.title}
                  id={category.id}
                  setSelectedCategoryId={setSelectedCategoryId}
                  isActive={category.id === selectedCategoryId}
                />
              ))}
            </div>
          )}
          <div css={styles.articlesGrid}>
            {featuredArticle && (
              <BlogArticle
                key={featuredArticle!.slug}
                {...featuredArticle}
                isFeatured
                newsOrBlog={newsOrBlog}
              />
            )}
            {blogArticlesFromActiveCategorySortedByDate.map(article => (
              <BlogArticle
                key={article.slug}
                {...article}
                newsOrBlog={newsOrBlog}
              />
            ))}
          </div>
        </motion.div>
      </ContentWrapper>
    </PageWrapper>
  );
};

export const BlogOverviewPage: FunctionComponent<GetBlogOverviewPagePropsResult> = ({
  blogArticles,
  blogOverviewPageProps,
  footer,
  header,
  seo
}) => (
  <BlogOverviewPageContent
    articleOverviewPageProps={blogOverviewPageProps}
    articles={blogArticles}
    seo={seo}
    footer={footer}
    header={header}
    newsOrBlog="blog"
  />
);

export const NewsOverviewPage: FunctionComponent<GetNewsOverviewPagePropsResult> = ({
  footer,
  header,
  newsArticles,
  newsOverviewPageProps,
  seo
}) => (
  <BlogOverviewPageContent
    articleOverviewPageProps={newsOverviewPageProps}
    articles={newsArticles}
    footer={footer}
    header={header}
    seo={seo}
    newsOrBlog="news"
  />
);
